import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { ArticleCTA } from "@/components/site/ArticleCTA";
import { trackCalendlyClick } from "@/lib/analytics";
import { IntentShiftCallout } from "@/components/site/IntentShiftCallout";
import { SystemBridgeCallout } from "@/components/site/SystemBridgeCallout";
import { Reveal } from "@/components/site/Reveal";
import { alternatePath, SITE_URL } from "@/lib/i18n";
import { buildHead, breadcrumbJsonLd, faqJsonLd, serviceJsonLd } from "@/lib/seo";
import { quotePath } from "@/lib/quote-links";
import coverImg from "@/assets/hub-marketing-strategy-1600.webp";
import cover1200 from "@/assets/hub-marketing-strategy-1200.webp";
import cover800 from "@/assets/hub-marketing-strategy-800.webp";

const PATH = "/es/estrategia-marketing-shopify";
const TITLE = "Estrategia de marketing para Shopify: playbook 2026";
const DESCRIPTION =
  "El marco que usamos para crecer tiendas Shopify Plus: las 5 capas de una estrategia real, un plan de 90 días para hacer crecer tu tienda Shopify, mix de…";

const FAQS = [
  {
    q: "¿Qué es una estrategia de marketing para Shopify?",
    a: "Es el sistema que conecta adquisición, conversión en sitio, retención, operaciones y medición alrededor de un único modelo de ingresos, diseñado para las palancas específicas que Shopify y Shopify Plus exponen (tema, checkout, apps, Shop Pay, Shop Channel, funciones Plus). No es una lista de tácticas; es el orden en que las arreglas y compones.",
  },
  {
    q: "¿Cómo hago crecer mi tienda Shopify de forma orgánica?",
    a: "Primero arregla lo estructural: SEO técnico (filtros de navegación, PLPs indexables, higiene de redirects 301), Core Web Vitals y arquitectura de información a nivel producto. Después suma contenido para queries informacionales y comparativas de alta intención, y usa Shop Channel + schema de reviews para componer visibilidad. La mayoría de las tiendas se estanca porque su presupuesto de rastreo se gasta en URLs filtradas, no porque les falte blog.",
  },
  {
    q: "¿Cuánto tarda en verse el resultado de una estrategia de marketing en Shopify?",
    a: "En una tienda bien diagnosticada: cambios de paid y CRO se ven en 2–4 semanas, mejoras de retención/email en 30–60 días, y SEO compone desde el mes 3. Replatformas o fixes técnicos de SEO (filtros, redirects) suelen producir un salto en 6–10 semanas cuando Google vuelve a rastrear.",
  },
  {
    q: "Shopify vs Shopify Plus, ¿cambia la estrategia?",
    a: "Sí. Plus desbloquea Shopify Functions, Checkout Extensibility, reemplazos de Scripts, B2B y Markets, lo que cambia cómo estructuras promociones, bundles y expansión internacional. Por debajo de ~$2M USD de GMV, Shopify estándar con un tema bien arquitectado suele alcanzar; por encima, las funciones Plus empiezan a pagarse solas.",
  },
  {
    q: "¿Cuál es el presupuesto de marketing correcto para una tienda Shopify de $1M–$10M?",
    a: "Un benchmark útil: 12–18% del revenue neto entre paid media, equipo (agencia o in-house), herramientas y contenido. La distribución cambia con la madurez, etapa temprana es paid-heavy, marcas maduras rebalancean hacia retención, marca y SEO. La pregunta equivocada es '¿cuánto debo gastar?' La correcta es '¿cuál es mi margen de contribución después de marketing y dónde es más eficiente el siguiente dólar?'",
  },
  {
    q: "¿Necesito SEO si ya estoy haciendo paid?",
    a: "Sí. Paid es tráfico rentado; orgánico es tráfico propio. Las tiendas Shopify más sanas con las que trabajamos están en 35–50% orgánico + directo, lo que baja el CAC blended y le da al negocio algo de qué agarrarse cuando los costos de ads suben. Además, el SEO compone, la página que publicas hoy sigue trabajando en 18 meses.",
  },
  {
    q: "¿Qué KPIs debe trackear una estrategia de marketing Shopify?",
    a: "Cinco números: margen de contribución después de marketing (no ROAS), CAC blended, tasa de recompra a 60 días, share orgánico + directo de sesiones, y conversion rate por template (PDP, PLP, colección). Todo lo demás es diagnóstico al servicio de estos cinco.",
  },
  {
    q: "¿Cuándo conviene contratar una agencia vs construir in-house?",
    a: "In-house para marca, cadencia de contenido, servicio al cliente y merchandising. Agencia para SEO técnico, performance, replatformas, sistemas de experimentación CRO y arquitectura de analítica, trabajo que requiere expertise profundo de plataforma pero no es un puesto de 40 horas semanales. Casi todas las tiendas de $1M–$20M corren un equipo in-house chico más un partner especializado en Plus.",
  },
];

const COVER_ABS = `${SITE_URL}/assets/hub-marketing-strategy-1600.webp`;
const COVER_SRCSET = `${cover800} 800w, ${cover1200} 1200w, ${coverImg} 1600w`;
const COVER_SIZES = "(min-width: 1024px) 1024px, 100vw";

export const Route = createFileRoute("/es/estrategia-marketing-shopify")({
  head: () =>
    buildHead({
      locale: "es",
      title: `${TITLE} · Shugert`,
      description: DESCRIPTION,
      path: PATH,
      altPath: alternatePath(PATH, "en"),
      ogImage: COVER_ABS,
      preloadImage: cover1200,
      preloadImageSrcset: COVER_SRCSET,
      preloadImageSizes: COVER_SIZES,
      jsonLd: [
        breadcrumbJsonLd([
          { name: "Inicio", path: "/es" },
          { name: "Estrategia de marketing Shopify", path: PATH },
        ]),
        faqJsonLd(FAQS),
        serviceJsonLd({
          name: "Estrategia de marketing para Shopify",
          description:
            "Estrategia, SEO técnico, CRO, performance y analítica para tiendas Shopify y Shopify Plus. Construimos el sistema que hace crecer el revenue sin hacer crecer el CAC.",
          path: PATH,
          serviceType: "Ecommerce Marketing Strategy",
          deliverables: [
            "Estrategia de adquisición + conversión + retención",
            "SEO técnico y arquitectura del sitio",
            "Mix de canales y asignación de presupuesto",
            "Arquitectura de analítica + atribución",
            "Roadmap de ejecución a 90 días",
          ],
          locale: "es",
        }),
      ],
    }),
  component: HubPage,
});

function HubPage() {
  return (
    <SiteShell>
      <MarketingStrategyHubEs />
    </SiteShell>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-muted-foreground">
      {children}
    </p>
  );
}

function MarketingStrategyHubEs() {
  const cta = quotePath("es");
  return (
    <article className="pb-32">
      <header className="mx-auto max-w-4xl px-6 pt-28 sm:pt-32">
        <SectionLabel>Guía pilar · 2026</SectionLabel>
        <h1 className="mt-5 font-display text-5xl font-semibold leading-[1.02] tracking-tight text-foreground sm:text-6xl">
          Estrategia de marketing para Shopify
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
          El marco que usa un operador senior para hacer crecer una tienda Shopify: las cinco
          capas de una estrategia real, un plan de 90 días para componer las ganancias, el mix
          de canales que funciona en Shopify en 2026, y los errores que mantienen a las tiendas estancadas.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <a
            href="https://calendly.com/snoriega/discovery-call"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackCalendlyClick({
                page: "estrategia-marketing-shopify",
                location: "hero",
                locale: "es",
                destination: "https://calendly.com/snoriega/discovery-call",
              })
            }
            className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-3 text-sm font-medium text-background transition-opacity hover:opacity-90"
          >
            Agendar diagnóstico
          </a>
          <Link
            to="/es/contacto"
            className="inline-flex items-center justify-center rounded-full border border-border px-6 py-3 text-sm font-medium text-foreground transition-colors hover:bg-card"
          >
            Hablar con el equipo
          </Link>
        </div>
        <p className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Trabajamos típicamente con tiendas Shopify y Shopify Plus con $500k+ USD en revenue anual.
        </p>
      </header>

      <div className="mx-auto mt-12 max-w-5xl px-6">
        <div className="overflow-hidden rounded-3xl border border-border">
          <img
            src={cover1200}
            srcSet={COVER_SRCSET}
            sizes={COVER_SIZES}
            alt="Ilustración editorial de una tienda Shopify con flechas de crecimiento, hub de estrategia de marketing Shopify de Shugert"
            width={1600}
            height={1024}
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="aspect-[16/10] w-full object-cover"
          />
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-3xl px-6">
        <Reveal>
          <p className="rounded-2xl border-l-4 border-foreground/30 bg-card/50 px-6 py-5 text-base font-medium leading-relaxed text-foreground sm:text-lg">
            Una estrategia de marketing para Shopify es el sistema que une adquisición,
            conversión en sitio, retención, operaciones y medición en un único modelo
            de revenue, construido alrededor de las palancas específicas que Shopify
            y Shopify Plus exponen. No es un stack de tácticas; es el orden en que
            las arreglas y compones. Bien hecha, baja el CAC blended, sube el margen
            de contribución y vuelve predecible el crecimiento. Mal hecha, se ve como
            un ROAS que pinta bien mientras el P&L se cae sin que nadie lo note.
          </p>
        </Reveal>
      </div>

      <Section eyebrow="El marco" id="cinco-capas">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Las 5 capas de una estrategia de marketing para Shopify
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Cada capa afecta al mismo número de revenue. La mayoría de los equipos las vende como servicios separados, nosotros las tratamos como un sistema. Puedes contratar cualquier capa de forma independiente, pero el diagnóstico siempre empieza desde el todo.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <LayerCard
            n="01"
            title="Adquisición"
            body="Paid media, SEO, partnerships, afiliados y superficies de búsqueda con IA. Acá el trabajo es traer demanda calificada a un CAC blended que el negocio se pueda permitir, no maximizar ROAS en un solo canal."
            linkLabel="Cómo corremos paid + SEO →"
            linkTo="/es/cro-shopify"
          />
          <LayerCard
            n="02"
            title="Conversión"
            body="PDP, PLP, colección, carrito, checkout. En Shopify es donde suelen estar las ganancias más grandes y rápidas, CRO a nivel template, Shop Pay, schema, search relevance y fixes estructurales que componen."
            linkLabel="CRO & SEO para Shopify →"
            linkTo="/es/cro-shopify"
          />
          <LayerCard
            n="03"
            title="Retención"
            body="Lifecycle de email/SMS, suscripciones, post-compra, lealtad. La tasa de recompra a 60 días es el número más subvaluado en la mayoría de los P&Ls Shopify, y casi siempre se puede arreglar."
            linkLabel="Retainer mensual →"
            linkTo="/es/retainer"
          />
          <LayerCard
            n="04"
            title="Operaciones"
            body="Arquitectura del tema, stack de apps, Shopify Functions, performance, integraciones. El equipo de marketing rara vez es dueño de esto, pero le pone un techo a qué tan rápido pueden moverse las demás capas."
            linkLabel="Performance Shopify →"
            linkTo="/es/rendimiento"
          />
          <LayerCard
            n="05"
            title="Medición"
            body="GA4, tracking server-side, atribución, margen de contribución. Si no ves los números correctos limpios, vas a optimizar la capa equivocada. Es donde silenciosamente se rompe la mayoría de las estrategias."
            linkLabel="Analítica & IA →"
            linkTo="/es/analitica-ai"
          />
        </div>
      </Section>

      <Section eyebrow="Ejecución" id="noventa-dias">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Cómo hacer crecer tu tienda Shopify: un marco de 90 días
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Esta es la cadencia que usamos en engagements nuevos. Asume que la tienda ya está
          en vivo y facturando, no es un plan de lanzamiento.
        </p>

        <div className="mt-10 space-y-10">
          <PhaseBlock
            phase="Días 0–30"
            title="Diagnosticar"
            items={[
              "Auditoría completa de SEO técnico, URLs indexables, filtros de navegación, cadenas de redirects, cobertura de schema.",
              "Baseline de Core Web Vitals (CrUX + lab) en templates de PDP, PLP y home.",
              "Auditoría de analítica + atribución: GA4, tracking server-side, modelo de margen de contribución.",
              "P&L canal por canal: CAC blended, ROAS y supuestos de incrementalidad.",
              "Entrevistas a clientes + revisión de comportamiento en sitio (heatmaps, session replays, términos de búsqueda).",
            ]}
            links={[
              { label: "Recursos técnicos", to: "/es/recursos" },
              { label: "Guía de filtros de navegación", to: "/es/recursos/shopify-filtros-presupuesto-rastreo" },
            ]}
          />
          <PhaseBlock
            phase="Días 30–60"
            title="Tapar las fugas"
            items={[
              "Shippear fixes estructurales de SEO: noindex/disallow en URLs filtradas, higiene de redirects, reparar schema.",
              "CRO a nivel template: buy box en PDP, densidad de PLP, fricción en carrito y checkout.",
              "Flujos de email lifecycle en vivo: browse abandon, cart abandon, post-compra, win-back.",
              "Apretar paid: matar campañas estructuralmente no rentables, reconstruir medición, subir el piso antes de subir el spend.",
              "Pase de performance sobre la imagen LCP, hydration y scripts de terceros.",
            ]}
            links={[
              { label: "CRO & SEO Shopify", to: "/es/cro-shopify" },
              { label: "Performance Shopify", to: "/es/rendimiento" },
            ]}
          />
          <PhaseBlock
            phase="Días 60–90"
            title="Componer las ganancias"
            items={[
              "Publicar contenido informacional de alta intención para las 10–20 queries que surgieron en el diagnóstico.",
              "Lanzar un backlog estructurado de experimentación sobre PDP / PLP / carrito, un test por template por sprint.",
              "Montar una vista semanal de P&L que ate el spend de marketing al margen de contribución, no al ROAS.",
              "Apretar los loops de retención: suscripción, lealtad, segmentación por comportamiento de recompra a 60 días.",
              "Preparar el roadmap de 6 meses: funciones Plus, headless si se paga solo, superficies de búsqueda con IA, internacionalización.",
            ]}
            links={[
              { label: "Migración a Shopify Plus", to: "/es/desarrollo-shopify" },
              { label: "Retainer mensual", to: "/es/retainer" },
            ]}
          />
        </div>
      </Section>

      <div className="mx-auto max-w-3xl px-6">
        <IntentShiftCallout locale="es" />
      </div>

      <Section eyebrow="Canales" id="canales">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          El mix de canales que funciona en Shopify en 2026
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
          No corremos todos para cada cliente. El mix depende de la categoría, el margen y dónde está el negocio en la curva de CAC. Pero este es el menú, y el orden de inversión que recomendamos en la mayoría de los engagements.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2">
          <ChannelCard
            title="Búsqueda orgánica + búsqueda con IA"
            body="Primero SEO técnico, después contenido. En 2026 esto también significa ser citable por superficies impulsadas por LLM, structured data, URLs limpias y párrafos de respuesta self-contained."
            linkLabel="Storefronts agénticos: qué cambia"
            linkTo="/es/recursos"
          />
          <ChannelCard
            title="Paid (Google + Meta)"
            body="Bidding con piso de utilidad atado al margen de contribución, no al ROAS. La velocidad de assets importa más que la estructura de cuenta, pero solo después de que la capa de medición sea honesta."
          />
          <ChannelCard
            title="Email + SMS (Klaviyo)"
            body="El revenue más barato por dólar en el stack. Los flujos lifecycle le ganan a las campañas one-off; la segmentación por comportamiento le gana a la segmentación por demografía."
          />
          <ChannelCard
            title="Shop Channel + reviews"
            body="Subvalorado. Shop Pay, Shop App y schema de reviews componen señales de confianza en cada superficie Shopify, y Google las premia cada vez más."
          />
          <ChannelCard
            title="Afiliados + creator partnerships"
            body="Alcance pagado por performance. Bien construido, se vuelve un 5–15% del revenue que es independiente de la volatilidad de las plataformas de ads."
            linkLabel="Guía de Shopify Collabs"
            linkTo="/es/recursos/how-to-use-shopify-collabs"
          />
          <ChannelCard
            title="Marca + redes orgánicas"
            body="El compound de largo plazo. No aparece limpio en atribución, aparece claro en búsqueda de marca, tráfico directo y conversion rate a 18 meses."
          />
        </div>
      </Section>

      <Section eyebrow="Qué evitar" id="errores">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Errores comunes de estrategia de marketing en Shopify
        </h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {[
            ["Optimizar ROAS en vez de margen de contribución", "El ROAS hace que el dashboard se vea bien. El margen paga al equipo. Divergen cuando el negocio escala, y la estrategia tiene que seguir al margen."],
            ["Indexar todas las URLs filtradas", "Los filtros de navegación abiertos queman presupuesto de rastreo en páginas infinitas de bajo valor y silenciosamente le ponen techo al ranking de tus PLPs principales."],
            ["Tratar el performance como un 'problema de ingeniería'", "Core Web Vitals es un problema de marketing disfrazado de ingeniería. LCP e INP mueven la tasa de conversión antes de mover el ranking."],
            ["Comprar más apps para tapar un hueco de estrategia", "Cada app es un impuesto, runtime, costo de render y atención del equipo. La mayoría de las tiendas necesita menos apps y mejor arquitectura, no más apps."],
            ["Replatformar porque el crecimiento se estancó", "Si la estrategia está rota, una plataforma nueva se rompe más rápido. Diagnostica primero, después decide si Plus / headless realmente desbloquean algo."],
            ["Ignorar la retención hasta que se rompe la adquisición", "El revenue más barato que vas a comprar es la segunda orden. Trata la retención como prioridad de Día 30, no como limpieza de Año 2."],
          ].map(([t, body]) => (
            <div key={t} className="rounded-2xl border border-border bg-card/40 p-6">
              <h3 className="font-display text-lg font-semibold text-foreground">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Cómo trabajamos" id="proceso">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Cómo Shugert construye una estrategia de marketing para Shopify
        </h2>
        <div className="mt-10 space-y-6">
          <ProcessStep
            n="01"
            title="Diagnóstico"
            body="Un operador senior audita tu tienda, tu analítica y el P&L por canal de punta a punta. Te llevas un diagnóstico escrito y un roadmap priorizado, usable con o sin nosotros."
            linkLabel="Agendar diagnóstico"
            linkTo="https://calendly.com/snoriega/discovery-call"
            onLinkClick={() =>
              trackCalendlyClick({
                page: "estrategia-marketing-shopify",
                location: "process_step",
                locale: "es",
                destination: "https://calendly.com/snoriega/discovery-call",
              })
            }
          />
          <ProcessStep
            n="02"
            title="Roadmap"
            body="Traducimos el diagnóstico a un plan de 90 días: fixes estructurales primero, ganancias que componen segundo, apuestas de crecimiento tercero. Dimensionado contra la capacidad real de tu equipo."
          />
          <ProcessStep
            n="03"
            title="Ejecución sobre PULSE"
            body="Nuestro sistema interno que shippea cambios validados de forma segura y continua, en CRO, SEO, performance y analítica. Reporte semanal atado al margen de contribución."
            linkLabel="Ver PULSE"
            linkTo="/es/pulse"
          />
        </div>
      </Section>

      <div className="mx-auto max-w-3xl px-6">
        <SystemBridgeCallout locale="es" />
      </div>

      <Section eyebrow="FAQ" id="faq">
        <h2 className="font-display text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
          Estrategia de marketing Shopify: preguntas frecuentes
        </h2>
        <div className="mt-8 divide-y divide-border">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer text-base font-semibold text-foreground sm:text-lg">
                {f.q}
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
                {f.a}
              </p>
            </details>
          ))}
        </div>
      </Section>

      <div className="mx-auto mt-20 max-w-3xl px-6">
        <ArticleCTA
          variant="footer"
          style="blue-glow"
          eyebrow="Diagnóstico de estrategia"
          title="¿Quieres una estrategia de marketing Shopify construida sobre tus números: no sobre un template?"
          body="Un operador senior audita tu tienda, tu analítica y tu P&L por canal, y te escribe un plan de 90 días que puedes correr con o sin nosotros."
          primaryLabel="Agendar diagnóstico"
          primaryHref="https://calendly.com/snoriega/discovery-call"
          onPrimaryClick={() =>
            trackCalendlyClick({
              page: "estrategia-marketing-shopify",
              location: "footer_cta",
              locale: "es",
              destination: "https://calendly.com/snoriega/discovery-call",
            })
          }
        />
      </div>
    </article>
  );
}

function Section({
  eyebrow,
  id,
  children,
}: {
  eyebrow: string;
  id: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mx-auto mt-24 max-w-5xl px-6 scroll-mt-28">
      <Reveal>
        <SectionLabel>{eyebrow}</SectionLabel>
        <div className="mt-4">{children}</div>
      </Reveal>
    </section>
  );
}

function LayerCard({
  n,
  title,
  body,
  linkLabel,
  linkTo,
}: {
  n: string;
  title: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-7">
      <p className="font-display text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">
        {n}
      </p>
      <h3 className="mt-3 font-display text-xl font-semibold text-foreground">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">{body}</p>
      {linkLabel && linkTo && (
        <Link
          to={linkTo}
          className="mt-4 inline-flex text-sm font-semibold text-foreground underline-offset-4 hover:underline"
        >
          {linkLabel}
        </Link>
      )}
    </div>
  );
}

function PhaseBlock({
  phase,
  title,
  items,
  links,
}: {
  phase: string;
  title: string;
  items: string[];
  links?: { label: string; to: string }[];
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/30 p-7 sm:p-8">
      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground">{phase}</p>
      <h3 className="mt-2 font-display text-2xl font-semibold text-foreground">{title}</h3>
      <ul className="mt-5 space-y-3">
        {items.map((it) => (
          <li key={it} className="flex gap-3 text-sm leading-relaxed text-foreground sm:text-base">
            <span aria-hidden className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-foreground/60" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
      {links && links.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-sm font-semibold text-foreground underline-offset-4 hover:underline"
            >
              {l.label} →
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelCard({
  title,
  body,
  linkLabel,
  linkTo,
}: {
  title: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-6">
      <h3 className="font-display text-lg font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
      {linkLabel && linkTo && (
        <Link
          to={linkTo}
          className="mt-3 inline-flex text-sm font-semibold text-foreground underline-offset-4 hover:underline"
        >
          {linkLabel} →
        </Link>
      )}
    </div>
  );
}

function ProcessStep({
  n,
  title,
  body,
  linkLabel,
  linkTo,
  onLinkClick,
}: {
  n: string;
  title: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
  onLinkClick?: () => void;
}) {
  return (
    <div className="flex gap-6 rounded-2xl border border-border bg-card/40 p-7">
      <span className="font-display text-2xl font-semibold text-muted-foreground">{n}</span>
      <div>
        <h3 className="font-display text-xl font-semibold text-foreground">{title}</h3>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">{body}</p>
        {linkLabel && linkTo && (
          (/^https?:\/\//i.test(linkTo) ? (
            <a
              href={linkTo}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onLinkClick}
              className="mt-3 inline-flex text-sm font-semibold text-foreground underline-offset-4 hover:underline"
            >
              {linkLabel} →
            </a>
          ) : (
            <Link
              to={linkTo}
              className="mt-3 inline-flex text-sm font-semibold text-foreground underline-offset-4 hover:underline"
            >
              {linkLabel} →
            </Link>
          ))
        )}
      </div>
    </div>
  );
}